/**
 * Map raw clean-recipe error signals (server `code`, raw message, native
 * Error) into short, human-friendly copy. Centralising this means every
 * surface — Index hero, region-mark fallback, paste flow — speaks the same
 * calm, actionable language instead of leaking server text like
 * "Edge Function returned a non-2xx status code".
 *
 * Keep messages:
 *  - Plain English, no codes
 *  - One sentence, with a clear next step where possible
 *  - Reassuring, never alarming
 */

const FALLBACK = "We couldn't process that recipe. Please try again.";

const CODE_MAP: Record<string, string> = {
  // Server-side codes from supabase/functions/clean-recipe/index.ts
  rate_limit: "We're a bit busy right now. Please wait a moment & try again.",
  no_credits: "We couldn't process that recipe right now. Please try again shortly.",
  ai_error: "Our recipe reader had a hiccup. Please try again.",
  no_response: "That doesn't look like a recipe we can process yet.",
  parse_error: "We couldn't read that clearly. Please try again.",
  not_a_recipe: "That doesn't look like a recipe we can process yet.",
  bad_input: "Please share a URL, photo, or paste the recipe text.",
  server_error: "Something interrupted the recipe reader. Please try again.",
  // Common URL-fetch failures returned by the edge function
  page_blocked: "We couldn't access that recipe. It may be behind a paywall.",
  paywall: "We couldn't access that recipe. It may be behind a paywall.",
  fetch_failed: "We couldn't open that link. Please check the URL & try again.",
  unsupported_url: "We can't read recipes from that site yet.",
  // Image-quality failures
  image_unreadable:
    "We couldn't read that image clearly. Try a sharper photo or different angle.",
  blurry_image:
    "We couldn't read that image clearly. Try a sharper photo or different angle.",
  // Timeout-style
  timeout: "This is taking longer than expected. Please try again.",
};

const matchesAny = (haystack: string, needles: string[]): boolean => {
  const lower = haystack.toLowerCase();
  return needles.some((n) => lower.includes(n));
};

export type FriendlyErrorInput = {
  code?: string | null;
  message?: string | null;
  /** Native Error or unknown thrown value, for network / abort detection. */
  cause?: unknown;
};

/**
 * Return a short, friendly message for a clean-recipe failure. Order:
 * explicit network/offline → known server code → message-text heuristics →
 * generic fallback. We never echo raw codes or stack traces.
 */
export const friendlyCleanRecipeError = ({
  code,
  message,
  cause,
}: FriendlyErrorInput): string => {
  // Offline / network — prefer this even if a code is also present.
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return "Connection issue — please check your internet & try again.";
  }
  const causeMsg =
    cause && typeof cause === "object" && "message" in cause
      ? String((cause as { message?: unknown }).message ?? "")
      : "";
  if (
    causeMsg &&
    matchesAny(causeMsg, ["failed to fetch", "networkerror", "network request failed"])
  ) {
    return "Connection issue — please check that you're online & try again.";
  }
  if (causeMsg && matchesAny(causeMsg, ["aborted", "timeout", "timed out"])) {
    return "This is taking longer than expected. Please try again.";
  }

  if (code && CODE_MAP[code]) return CODE_MAP[code];

  const text = (message || "").trim();
  if (text) {
    if (matchesAny(text, ["timeout", "timed out", "took too long"])) {
      return "This is taking longer than expected. Please try again.";
    }
    if (matchesAny(text, ["paywall", "blocked", "forbidden", "403"])) {
      return "We couldn't access that recipe. It may be behind a paywall.";
    }
    if (matchesAny(text, ["blur", "unreadable", "couldn't read", "could not read"])) {
      return "We couldn't read that image clearly. Try a sharper photo or different angle.";
    }
    if (matchesAny(text, ["not a recipe", "doesn't look like", "no recipe"])) {
      return "That doesn't look like a recipe we can process yet.";
    }
    if (matchesAny(text, ["network", "offline", "connection"])) {
      return "Connection issue — please check your internet & try again.";
    }
    // If the server already returned friendly copy (no "Edge Function…"
    // prefix and not obviously technical), pass it through.
    if (
      !/edge function/i.test(text) &&
      !/non-2xx/i.test(text) &&
      text.length < 160
    ) {
      return text;
    }
  }

  return FALLBACK;
};
